import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EditableGridService, SystemApiService } from '@cv-invision/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ORIGIN, LOGS } from '../nexsys-monitor/model/nexsys-monitor.constant';

export interface OriginSourceData {
	isDirty?: boolean;
	origin?: string;
	reload?: boolean;
	isHeaderChange?: boolean;
}
@Injectable()
export class NexsysMonitorService extends EditableGridService {
	private reloadListSubject = new BehaviorSubject({});
	private reloadListListener = this.reloadListSubject.asObservable();

	constructor(private httpClient: HttpClient, private systemApiService: SystemApiService) {
		super();
	}

	/**
	 * Sets new value for the reload list listener
	 * @param reload = true for reloading otherwise doesn't reload the list.
	 */
	reloadList(dataSet: OriginSourceData): void {
		this.reloadListSubject.next(dataSet);
	}

	/**
	 * Returns the reload list listener
	 */
	getReloadListListener(): Observable<any> {
		return this.reloadListListener;
	}

	/**
	 * GET - Return list of nexsys logs.
	 */
	getLogs(filter: any, source: string, apiVersion = '1'): Observable<any> {
		const url = this.getProviderURL(source);
		let httpParams: any = new HttpParams();
		const {
			transactionId,
			providerOrderIdentifier,
			xpanseSPNId,
			xpanseTransactionId,
			nexsysOrderId,
			lendingQbOrderId,
			classOrderId,
			fncOrderId,
			eventName,
			clientId,
			limit,
			read
		} = filter;

		// Helper function to append parameters if they exist
		const appendParam = (param: string, value: any) => {
			if (value) {
				httpParams = httpParams.append(param, value.toString());
			}
		};

		Object.keys(filter).forEach(filterKey => {
			if (['nexsysOrderId', 'read'].indexOf(filterKey) === -1) {
				appendParam(filterKey, filter[filterKey]);
			}
		});

		appendParam('api-version', apiVersion);

		if (nexsysOrderId) {
			const providerOrderId = (() => {
				switch (source) {
					case ORIGIN.EVO:
						return 'evoOrderId';
					case ORIGIN.Nexsys:
						return 'nexsysOrderId';
					default:
						return 'valutrustOrderId';
				}
			})();
			appendParam(providerOrderId, nexsysOrderId);
		}

		if (read && read.id !== 'All') {
			httpParams = httpParams.append('Read', read.id === 'Read');
		}

		const endpoint = source === ORIGIN.NexsysLegacy ? '/orphan-transactions/logs' : '/transactions/logs';

		return this.httpClient.get(`${url}${endpoint}`, {
			params: httpParams
		});
	}

	/* Get provider URL
 	 * @param source
 	 * @returns
 	 */
	getProviderURL(source: string): string {
        let url = '';

        switch (source) {
            case ORIGIN.Nexsys:
                url = this.systemApiService.api.nexsys;
                break;
            case ORIGIN.Valutrust:
                url = this.systemApiService.api.valutrust;
                break;
            case ORIGIN.Xpanse:
                url = this.systemApiService.api.xpanse;
                break;
            case ORIGIN.OrderExternal:
                url = this.systemApiService.api.orderExternal;
                break;
            case ORIGIN.FNC:
                url = this.systemApiService.api.fnc;
                break;
            case ORIGIN.EVO:
                url = this.systemApiService.api.evo;
                break;
			case ORIGIN.ValueLink:
				url = this.systemApiService.api.valueLink;
				break;
			case ORIGIN.ValueLinkLender:
				url = this.systemApiService.api.valueLinkLender;
				break;
			case ORIGIN.Connexions:
				url = this.systemApiService.api.connexions;
				break;
			case ORIGIN.Lendingqb:
				url = this.systemApiService.api.lendingqb;
				break;
			case ORIGIN.LandGorilla:
				url = this.systemApiService.api.landgorilla;
				break;
			case ORIGIN.Fics:
				url = this.systemApiService.api.fics;
				break;
			case ORIGIN.LendingPad:
				url = this.systemApiService.api.lendingpad;
				break;
        }

        return url;
    }

	/**
	 * Get content type
	 * @param source
	 * @returns
	 */
	getContentType(data: string): string {
		try {
			JSON.stringify(JSON.parse(data), null, 3);
			return 'application/json';
		} catch (e) {
			return 'application/xml';
		}
	}

	/**
	 * GET - Return list of orders .
	 */
	getNexsysOrders(filter: any): Observable<any> {
		let httpParams: any = new HttpParams();
		const {
			transactionId,
			origin,
			providerId,
			orderId,
			xpanseLoanNumber,
			provider,
			limit,
			acuityId,
			filenumber,
			isAddendum
		} = filter;

		if (transactionId) {
			httpParams = httpParams.append('transactionId', transactionId.toString());
		}
		if (origin) {
			httpParams = httpParams.append('origin', origin.toString());
		}
		if (provider) {
			httpParams = httpParams.append('provider', provider.toString());
		}
		if (providerId) {
			httpParams = httpParams.append('providerId', providerId.toString());
		}
		if (xpanseLoanNumber) {
			httpParams = httpParams.append('providerExternalId', xpanseLoanNumber.toString());
		}
		if (orderId) {
			httpParams = httpParams.append('id', orderId.toString());
		}
		if (filenumber) {
			httpParams = httpParams.append('AppScopeAppraisalFileNumber', filenumber.toString());
		}
		if (acuityId) {
			httpParams = httpParams.append('acuityId', acuityId.toString());
		}
		if (limit) {
			httpParams = httpParams.append('limit', limit.toString());
		}
		if (isAddendum) {
			httpParams = httpParams.append('isAddendum', isAddendum);
		}
		return this.httpClient.get(`${this.systemApiService.api.orders}/orders`, {
			params: httpParams
		});
	}

	/**
	 * get nexsys trans log
	 * @param id
	 */
	getOrderDetail(id: string): Observable<any> {
		return this.httpClient.get(`${this.systemApiService.api.orders}/orders/${id}`);
	}

	/**
	 * get nexsys trans log
	 * @param id
	 */
	getNexsysOrderTransLog(id: string, source: string, externalId: string = ''): Observable<any> {
		const url = this.getProviderURL(source);
		source = source === 'Nexsys Legacy' ? 'Nexsys' : source;
		let apiUrl: string;
		switch (source) {
			case LOGS.XPANSE:
				apiUrl = `${url}/transactions/${source.toLocaleLowerCase()}/${id}/${externalId}`;
				break;
			case LOGS.VALUELINK:
				apiUrl = `${url}/transactions/ValueLinkDocuments/${id}/${externalId}`;
				break;
			case LOGS.VALUELINK_LENDER:
			case LOGS.ORDERSEXTERNAL:
			case LOGS.CONNEXIONS:
			case LOGS.LENDINGQB:
			case LOGS.LANDGORILLA:
			case LOGS.FICS:
			case LOGS.LENDINGPAD:
				apiUrl = `${url}/transactions/class/${id}`;
				break;
			default:
				apiUrl = `${url}/transactions/${source.toLocaleLowerCase()}/${id}`;
				break;
		}

		return this.httpClient.get(apiUrl);
	}

	/**
	 * get nexsys trans log
	 * @param id
	 */
	retryNexsysOrderTransations(
		Orderid: string,
		method: string,
		payload: any,
		source: string,
		serviceId: string = null,
		apiVersion = '2',
		extraData: any
	): Observable<any> {
		const url = this.getProviderURL(source);
		const contentType = this.getContentType(payload);
		let methodUrl = '';
		const queryParams = [];

		if (apiVersion) {
			queryParams.push(`api-version=${apiVersion}`);
		}

		Object.keys(extraData).forEach(key => {
			queryParams.push(`${key}=${extraData[key]}`);
		});

		switch (source) {
			case LOGS.ORDERSEXTERNAL:
				methodUrl = `${url}/${method}`;
				break;
			case LOGS.EVO:
				methodUrl = `${url}/${source.toLowerCase()}/${Orderid}/${serviceId}/${method}`;
				break;
			case LOGS.VALUELINK:
			case LOGS.VALUELINK_LENDER:
			case LOGS.CONNEXIONS:
			case LOGS.LENDINGQB:
			case LOGS.LANDGORILLA:
			case LOGS.FICS:
			case LOGS.LENDINGPAD:
				methodUrl = `${url}/${source.toLowerCase()}/${method}`;
				break;
			default:
				methodUrl = `${url}/${source.toLowerCase()}/${Orderid}/${method}`;
				break;
		}

		const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

		return this.httpClient.post(
			`${methodUrl}${queryString}`,
			payload,
			{ headers: new HttpHeaders({ 'Content-Type': contentType }) }
		);
	}

	/**
	 * re enqueue order for acuity and appscope
	 * @param id
	 */
	reEnqueueOrderTransations(url: string, payload: any): Observable<any> {
		return this.httpClient.post(`${this.systemApiService.api.orders}/messages/${url}`, payload, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })
		});
	}

	/**
	 * get nexsys trans log
	 * @param id
	 */
	retryUpdateNexsysOrderTransations(
		Orderid: string,
		method: string,
		payload: any,
		source: string,
		extraData: any
	): Observable<any> {
		const url = this.getProviderURL(source);
		const contentType = this.getContentType(payload);
		const queryParams = [];

		Object.keys(extraData).forEach(key => {
			queryParams.push(`${key}=${extraData[key]}`);
		});

		const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

		return this.httpClient.put(
			`${url}/${source.toLocaleLowerCase()}/${Orderid}/${method}${queryString}`,
			payload,
			{ headers: new HttpHeaders({ 'Content-Type': contentType }) }
		);
	}

	/**
	 * Mark order log as read/unread
	 * @param id
	 */
	markOrderLogAsReadUnread(
		logId: string,
		payload: any
	): Observable<any> {
		return this.httpClient.put(
			`${this.systemApiService.api.nexsys}/orphan-transactions/logs/${logId}`,
			payload,
			{ headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
		);
	}

	/**
	 * Used to retrieve blob response from server api.
	 * @param location
	 * @param options
	 */
	downloadFile(location: string): Observable<any> {
		let httpParams: any = new HttpParams();

		httpParams = httpParams.append('location', location);

		return this.httpClient
			.get(`${this.systemApiService.api.nexsys}/orphan-transactions/logs/file`, {
				params: httpParams,
				responseType: 'blob'
			})
			.pipe(
				map((response: any) => {
					const result = {
						blob: new Blob([response], { type: 'application/pdf' }),
						filename: 'pdfDoc'
					};
					return result;
				})
			);
	}
}
